<template>
  <d-container
    fluid
    class="main-content-container px-0 px-md-2 px-lg-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        sm="12"
        md="9"
        lg="9"
        class="text-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Zmiany</span>
        <h3 class="page-title">Wykonania w trakcie zmiany</h3>
      </d-col>
      <d-col
        col
        sm="12"
        md="3"
        lg="3"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/operatorzy-i-maszyny/nowy')">
          <i class="material-icons">add</i>
          Dodaj wpis
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="allDataLoaded"
      style="width: 100%"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="working-stats-filters-form"
        tag="form"
        style="width: 100%"
        class="d-flex mb-2">
        <v-select
          style="min-width: 25%;"
          class="mr-1 mb-2 mt-auto"
          :options="allWorkers"
          v-model="filters.user_id"
          :reduce="user => user.id"
          placeholder="Wybierz pracownika"
          :searchable="false"
          label="name">
        </v-select>

        <div
          class="w-100 mb-2 mr-1"
          style="width: 25%;min-width: 200px;max-width: 220px;">
          <label
            for="dateStart"
            class="mb-0">
            <small>Data:</small>
          </label>
          <div
            :class="{
              'form-control is-date-range vs-wrap-text w-100 text-center': true,
              'is-disabled': false
          }">
            <date-range-picker
              ref="picker"
              :locale-data="{
                'firstDay': 1,
                'format': 'DD-MM-YYYY',
                'daysOfWeek': ['Ndz', 'Pon', 'Wto', 'Śro', 'Czw', 'Ptk', 'Sob'],
                'monthNames': ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
                'applyLabel': 'OK',
                'cancelLabel': 'Anuluj'
              }"
              style="width: auto;"
              v-model="dateRange"
              :singleDatePicker="true"
              :timePicker="false"
              :showWeekNumbers="false"
              :showDropdowns="false"
              :ranges="false"
              :linkedCalendars="true"
              :auto-apply="true">
              <template v-slot:input="picker">
                {{ picker.startDate | dateFilter }}
              </template>
            </date-range-picker>
          </div>
        </div>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-auto mr-1 mb-2 mt-auto"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap mb-2 mt-auto"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0 align-middle"
              style="width: 40px;"></th>
            <th
              scope="col"
              class="border-0 align-middle">Operator</th>
            <th
              scope="col"
              class="border-0 align-middle text-center">Data zmiany</th>
            <th
              scope="col"
              class="border-0 align-middle text-center">Czas<br>zmiany</th>
            <th
              scope="col"
              class="border-0 align-middle text-center">Przerwa</th>
            <th
              scope="col"
              class="border-0 align-middle text-center">Liczba/czas<br>zmian</th>
            <th
              scope="col"
              class="border-0 align-middle text-center"
              title="Estymowany czas na podstawie czasu próby">
              Estymowany<br>czas
            </th>
            <th
              scope="col"
              class="border-0 align-middle text-right">Estymowane<br>wykonanie</th>
            <th
              scope="col"
              class="border-0 align-middle text-right"
              style="width: 140px;">Suma wykonań<br>haftów</th>
            <th
              scope="col"
              class="border-0 align-middle text-center"
              style="width: 100px;">Realizacja</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded">
          <template v-for="(item, index) of workingStats">
            <tr
              :key="'main-' + index"
              :class="{'bg-light': expandedRows.includes(index)}">
              <td
                class="align-middle text-center py-lg-1 py-md-3 py-3"
                style="cursor: pointer; width: 40px;"
                @click="toggleRow(index)">
                <i class="material-icons text-muted">
                  {{ expandedRows.includes(index) ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                </i>
              </td>
              <td class="align-middle py-lg-1 py-md-3 py-3 font-weight-bold text-dark">
                <label class="table-mobile-label mb-1">Operator</label>
                {{ item.user_name || '-' }}
              </td>
              <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                <label class="table-mobile-label mb-1">Data zmiany</label>
                {{ formatTime(item.working_hour_start_at) }} - {{ formatTime(item.working_hour_end_at) }}<br>
                <small class="text-muted">
                  {{ formatDateString(item.working_hour_date) }}
                </small>
              </td>
              <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                <label class="table-mobile-label mb-1">Czas zmiany</label>
                {{ item.working_hour_minutes }} min
              </td>
              <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                <label class="table-mobile-label mb-1">Przerwa</label>
                {{ item.break_minutes }} min
              </td>
              <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                <label class="table-mobile-label mb-1">Liczba/czas zmian</label>
                {{ item.changes_count }} <span class="text-primary">/ {{ item.changes_minutes }} min</span>
              </td>
              <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                <label class="table-mobile-label mb-1">Estymowany czas</label>
                {{ item.estimate_minutes }} min
              </td>
              <td class="align-middle text-right py-lg-1 py-md-3 py-3">
                <label class="table-mobile-label mb-1">Estymowane wykonanie</label>
                {{ item.estimate_execution || 0 }}
              </td>
              <td
                class="align-middle text-right py-lg-1 py-md-3 py-3 font-weight-bold text-primary">
                <label class="table-mobile-label mb-1">Suma wykonań haftów</label>
                {{ item.execution_sum }}
              </td>
              <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                <label class="table-mobile-label mb-1">Realizacja</label>
                <span :class="['badge', 'badge-pill', getBadgeClass(item.estimate_execution, item.execution_sum)]">
                  {{ getCompletionPercentage(item.estimate_execution, item.execution_sum) }}
                </span>
              </td>
            </tr>

            <tr
              :key="'details-' + index"
              v-if="expandedRows.includes(index)"
              class="bg-light">
              <td
                colspan="10"
                class="px-4 pt-0 pb-3 border-top-0">

                <div class="pt-2 px-3 pb-3 bg-white rounded border shadow-sm">
                  <h6
                    class="mb-2 text-muted text-uppercase"
                    style="font-size: 12px; letter-spacing: 0.5px;">
                    Szczegóły wykonań
                  </h6>
                  <table
                    class="table table-sm table-bordered bg-white table-striped mb-0"
                    style="font-size: 13px;">
                    <thead class="bg-light">
                      <tr>
                        <th class="border-0">Zamówienie</th>
                        <th class="border-0">Kod produktu</th>
                        <th class="border-0">Zdobienie</th>
                        <th class="border-0">Maszyna</th>
                        <th
                          class="border-0 text-right"
                          style="width: 140px;">Liczba wykonań</th>
                        <th
                          class="border-0"
                          style="width: 60px;"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(execution, eIndex) in item.executions"
                        :key="'exec-' + eIndex">
                        <td class="align-middle">{{ execution.order_machine.order_service.order_product.order.order_name || '-' }}</td>
                        <td class="align-middle">
                          <code class="px-0">{{ execution.order_machine.order_service.order_product.product_code || '-' }}</code>
                        </td>
                        <td class="align-middle">{{ execution.order_machine.order_service.service_name || '-' }}</td>
                        <td class="align-middle">{{ execution.order_machine.machine_name || '-' }}</td>
                        <td class="align-middle text-right font-weight-bold">{{ execution.execution || '-' }}</td>
                        <td></td>
                      </tr>
                      <tr v-if="!item.executions || item.executions.length === 0">
                        <td
                          colspan="6"
                          class="text-center text-muted py-3">
                          Brak szczegółów wykonań.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="workingStats.length === 0">
            <td
              colspan="10"
              class="text-center">
              Brak pozycji do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się.
        <a
          href="javascript:window.location.reload();"
          class="alert-link">
          Odśwież stronę
        </a>
        aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination :meta-data="paginationMetaData" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from '@/utils/FormUtils.js';
import CalendarUtils from '@/utils/CalendarUtils.js';
import CustomDecimals from '@/utils/CustomDecimals.js';
import HasFilters from '@/mixins/HasFilters.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import ListUtils from '@/utils/ListUtils.js';
import Pagination from '@/components/common/Pagination.vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  name: 'working-stats',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination,
    'date-range-picker': DateRangePicker
  },
  filters: {
    dateFilter (input) {
      if (input === null) {
        return ' szukaj w dniu ';
      }

      return [
        input.getFullYear(),
        input.getMonth() + 1,
        input.getDate()
      ]
        .map(n => (n < 10 ? '0' + n : n))
        .join('-');
    }
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedWorkers;
    }
  },
  data () {
    return {
      allWorkers: [],
      dateRange: {
        endDate: null,
        startDate: null
      },
      expandedRows: [],
      filters: {
        user_id: null
      },
      loaded: false,
      loadedWorkers: false,
      loadError: false,
      removeInProgress: false,
      workingStats: []
    };
  },
  mounted () {
    this.loadAdditionalData();
    this.loadFilteredData();
  },
  methods: {
    toggleRow (index) {
      const position = this.expandedRows.indexOf(index);
      if (position !== -1) {
        this.expandedRows.splice(position, 1);
      } else {
        this.expandedRows.push(index);
      }
    },
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {};
      this.expandedRows = [];

      if (this.filters.user_id) {
        where.user_id = this.filters.user_id;
      }

      if (this.dateRange.startDate) {
        where.date = CalendarUtils.formatDateString(this.dateRange.startDate);
      }

      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: '/api/users/working-stats/items?page=' + this.currentPage,
        listField: 'workingStats',
        pagination: true,
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        user_id: null
      };
      this.dateRange.startDate = null;
      this.dateRange.endDate = null;
      this.expandedRows = [];

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['working-stats-filters-form'], this.debouncedLoadFilteredData);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/users/items',
        method: 'get',
        outputKey: 'allWorkers',
        loadedKey: 'loadedWorkers',
        errorKey: 'loadError',
        params: {
          pagination: 0,
          where: {
            active: 1,
            type: 'OPERATOR'
          }
        }
      });
    },
    formatDateString (date) {
      return CalendarUtils.formatDateString(date);
    },
    formatTime (timeStr) {
      if (!timeStr) {
        return '-';
      }
      return timeStr.substring(0, 5);
    },
    getCompletionPercentage (estimate, actual) {
      if (!estimate || estimate === 0) {
        return '---';
      }
      const percent = (actual / estimate) * 100;

      return CustomDecimals.roundProperly(percent, 1) + '%';
    },
    getBadgeClass (estimate, actual) {
      if (!estimate || estimate === 0) {
        return 'badge-light text-muted';
      }
      const percent = (actual / estimate) * 100;

      if (percent >= 95) {
        return 'badge-success';
      }
      if (percent >= 75) {
        return 'badge-warning';
      }
      return 'badge-danger';
    },
  }
};
</script>

<style lang="scss">
.page-filters form {
  width: 100%;
}

@media screen  and (max-width: 1230px) {
  .page-filters {
    form {
      flex-wrap: wrap;

      input {
        margin-right: 0!important;
        min-width: unset!important;
        width: 100%;
      }
    }
  }
}

@media screen  and (max-width: 768px) {
  .page-filters {
    form {
      flex-wrap: wrap;

      .v-select {
        margin-right: 0!important;
        max-width: unset!important;
        width: 100%!important;
      }
    }
  }
}

</style>
